import { Box, Button, Chip, Paper, Skeleton, Stack, Typography } from "@mui/material";
import {
  CalendarMonthRounded,
  CardGiftcardRounded,
  LocationOnRounded,
  PersonRounded,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../../../service/Api";
import type { RootState } from "../../../../../store/Index";
import type { MemoryBook } from "../../../../../types/memory-table";
import { IStreamDetails } from "../../../../../types/streams";
import DynamicThumbnailCard from "../../thumbnail/ThumbnailCard";
import { AssignMemoryBookModal } from "./AssignMemoryBookModal";

const BOOK_STATUS_META: Record<
  NonNullable<MemoryBook["status"]>,
  { label: string; color: string }
> = {
  pending: { label: "Pending", color: "#94a3b8" },
  "in-progress": { label: "In Progress", color: "#3b82f6" },
  done: { label: "Done", color: "#10b981" },
  printing: { label: "Printing", color: "#8b5cf6" },
};

type Props = {
  streamId?: string;
};

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          bgcolor: "#f7f0ed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{ fontSize: "11px", color: "text.secondary", lineHeight: 1 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", mt: 0.3 }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

const BookingAndDeceasedDetails: React.FC<Props> = ({ streamId }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [streamDetails, setStreamDetails] = useState<IStreamDetails | null>(
    null,
  );
  const [slug, setSlug] = useState<string | null>("");
  const [bookStatus, setBookStatus] = useState<MemoryBook["status"] | null>(null);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [thumbnailRefreshTrigger] = useState(0);
  const params = useParams<{ id: string }>();

  const resolvedStreamId = useMemo(
    () => streamId ?? params.id ?? "",
    [streamId, params.id],
  );

  const fetchBookingDetails = useCallback(async () => {
    if (!resolvedStreamId) return;
    setError(false);
    setIsLoading(true);
    try {
      const response = await apiService.get<IStreamDetails>(
        `/api/streams/stream/${resolvedStreamId}/`,
      );
      setStreamDetails(response.data);
    } catch (error) {
      console.error("Failed to load stream:", error);
      toast.error("Failed to load stream. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [resolvedStreamId]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  useEffect(() => {
    if (!resolvedStreamId) return;
    apiService
      .get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${resolvedStreamId}`,
      )
      .then((res) => setBookStatus(res.data.results[0]?.status ?? null))
      .catch(() => setBookStatus(null));
  }, [resolvedStreamId]);

  useEffect(() => {
    if (!streamDetails) return;
    const deceased = streamDetails.booking?.deceased?.[0];
    const nameSource = deceased
      ? `${deceased.first_name} ${deceased.surname}`
      : streamDetails.title;
    if (!nameSource) return;
    const slugified = nameSource
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    setSlug(slugified);
  }, [streamDetails]);

  const deceased = streamDetails?.booking?.deceased?.[0];

  const deceasedName = deceased
    ? `${deceased.first_name} ${deceased.surname}`
    : (streamDetails?.title ?? "N/A");

  const years =
    deceased?.date_of_birth && deceased?.date_of_death
      ? `${new Date(deceased.date_of_birth).getFullYear()} – ${new Date(deceased.date_of_death).getFullYear()}`
      : null;

  const streamDate = streamDetails?.date
    ? new Date(streamDetails.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "N/A";

  const packageName = streamDetails?.booking?.package_item?.name ?? "N/A";

  const contactPerson = streamDetails?.booking?.contact
    ? `${streamDetails.booking.contact.first_name} ${streamDetails.booking.contact.last_name}`
    : "N/A";

  const venueName =
    streamDetails?.service?.venue?.name ?? streamDetails?.address_one ?? "N/A";

  if (!resolvedStreamId) {
    return (
      <Paper sx={{ borderRadius: 3, p: 2.5, boxShadow: 0, mb: 2 }}>
        <Typography>Stream ID missing.</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        backgroundColor: reduxTheme.primary_colors[900],
        borderRadius: 3,
        p: 2.5,
        boxShadow: 0,
        mb: 2,
      }}
    >
      {isLoading && (
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="30%" height={20} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
            <Stack spacing={1.5} flex={1}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Stack>
            <Skeleton
              variant="rounded"
              sx={{
                width: { xs: "100%", sm: 200 },
                height: { xs: 80, sm: 113 },
              }}
            />
          </Stack>
        </Stack>
      )}

      {error && !isLoading && (
        <Box pt={1} pb={1}>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "13px",
              textAlign: "center",
            }}
          >
            Something went wrong collecting details
          </Typography>
          <Box display="flex" justifyContent="center" mt={1}>
            <Button onClick={fetchBookingDetails} variant="contained">
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {!isLoading && !error && streamDetails && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            alignItems="flex-start"
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "20px",
                    color: "#1e293b",
                    lineHeight: 1.2,
                  }}
                >
                  {deceasedName}
                </Typography>
                {bookStatus && (
                  <Chip
                    size="small"
                    label={`Book: ${BOOK_STATUS_META[bookStatus].label}`}
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      bgcolor: BOOK_STATUS_META[bookStatus].color,
                    }}
                  />
                )}
              </Stack>
              {years && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 800,
                    color: reduxTheme.primary_colors[400],
                    mt: 0.3,
                    mb: 2,
                  }}
                >
                  {years}
                </Typography>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  mt: years ? 0 : 2,
                }}
              >
                <InfoRow
                  icon={
                    <CalendarMonthRounded
                      sx={{ fontSize: 16, color: "#724e41" }}
                    />
                  }
                  label="Stream Date"
                  value={streamDate}
                />
                <InfoRow
                  icon={
                    <LocationOnRounded
                      sx={{ fontSize: 16, color: "#724e41" }}
                    />
                  }
                  label="Venue"
                  value={venueName}
                />
                <InfoRow
                  icon={
                    <CardGiftcardRounded
                      sx={{ fontSize: 16, color: "#724e41" }}
                    />
                  }
                  label="Package"
                  value={packageName}
                />
                <InfoRow
                  icon={
                    <PersonRounded sx={{ fontSize: 16, color: "#724e41" }} />
                  }
                  label="Contact"
                  value={contactPerson}
                />
              </Box>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                width: { xs: "100%", sm: 200 },
                height: { xs: "auto", sm: 113 },
                borderRadius: "10px",
                overflow: "hidden",
                position: "relative",
                display: { xs: "none", sm: "block" },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transformOrigin: "top left",
                  transform: `scale(${200 / 350})`,
                }}
              >
                <DynamicThumbnailCard
                  refreshTrigger={thumbnailRefreshTrigger}
                  streamData={streamDetails}
                  streamId={streamDetails.id}
                  height={Math.round((350 * 720) / 1280)}
                  width={350}
                />
              </Box>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1.25}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 2.5 }}
          >
            {slug && (
              <Button
                sx={{
                  backgroundColor: "info.main",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  flex: { xs: "1 1 45%", sm: "0 1 auto" },
                }}
                variant="contained"
                href={`/memorial-page/${streamDetails.booking.id}/${slug}/`}
              >
                Memorial Page
              </Button>
            )}
            <Button
              sx={{
                backgroundColor: "#AF52DE",
                borderRadius: "8px",
                fontSize: "0.8rem",
                flex: { xs: "1 1 45%", sm: "0 1 auto" },
              }}
              variant="contained"
              href={`/bookings/${streamDetails.booking.id}`}
            >
              Booking Details
            </Button>
            <Button
              sx={{
                borderRadius: "8px",
                fontSize: "0.8rem",
                flex: { xs: "1 1 45%", sm: "0 1 auto" },
              }}
              variant="contained"
              href={`/stream-management/view/${streamDetails.id}`}
            >
              Stream Details
            </Button>
            <Button
              sx={{
                backgroundColor: "success.main",
                borderRadius: "8px",
                fontSize: "0.8rem",
                flex: { xs: "1 1 45%", sm: "0 1 auto" },
              }}
              variant="contained"
              onClick={() => setOpenAssignModal(true)}
            >
              Assign Book
            </Button>
          </Stack>
        </>
      )}

      <AssignMemoryBookModal
        open={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        deceasedName={
          streamDetails?.booking?.deceased?.[0]
            ? `${streamDetails.booking.deceased[0].first_name} ${streamDetails.booking.deceased[0].surname}`
            : streamDetails?.title || ""
        }
        streamId={streamDetails?.id ?? 0}
      />
    </Paper>
  );
};

export default BookingAndDeceasedDetails;
